/**
 * ============================================================================
 * ACTIVATION REPORTS — completeness and demo arithmetic
 * ============================================================================
 *
 * Completeness is DERIVED, not stored:
 *
 *   section %  = (auto-filled + completed manual) ÷ all inputs on that section
 *   report %   = the same, summed across the enabled sections
 *
 * Partnership Net 2.0 hardcoded a percentage per section, so editing a section
 * never moved the number and the library card disagreed with the builder.
 *
 * This module also repairs two arithmetic faults in the worked demo. Both were
 * in the presentation rather than the data, so both are fixed here:
 *
 *   `rescaleMonthly`     the monthly array held bar-height weights but was
 *                        labelled as money, and summed to roughly six times the
 *                        season total.
 *   `averageReachPerMatch` the competition table's reach column was totalled as
 *                        if it were a sum. It is a per-match average.
 */

import type {
  ActivationReport,
  ActivationSection,
  ActivationStatus,
  DemoActivationReport,
  DemoCompetition,
} from '@/data/contracts';
import type { StatusKind } from '@/lib/bands';
import { mean, round, sum } from '@/lib/math';
import { rng, seedFrom } from '@/lib/random';

const EMPTY_SECTION = (id: string): ActivationSection => ({
  id,
  name: id,
  required: false,
  manual: 0,
  auto: 0,
});

export const findSection = (
  sections: readonly ActivationSection[],
  id: string,
): ActivationSection => sections.find((s) => s.id === id) ?? EMPTY_SECTION(id);

/**
 * Fraction of a section's MANUAL inputs that are complete, 0–1.
 *
 * Precedence: a finished report is 100%; an explicit builder value wins next; a
 * section with no manual inputs is complete the moment it is created; otherwise
 * a deterministic 25–95% seeded from the report and section id, so the demo
 * library shows a realistic spread that does not change between reloads.
 */
export function sectionFill(
  report: ActivationReport,
  sectionId: string,
  section: ActivationSection,
): number {
  if (report.complete === true) return 1;
  const explicit = report.filled?.[sectionId];
  if (explicit !== undefined) return explicit;
  if (section.manual === 0) return 1;
  return round(0.25 + seedFrom(report.id + sectionId) * 0.7, 2);
}

/** Completion of one section, as a whole percentage. */
export function sectionPercent(
  report: ActivationReport,
  sectionId: string,
  sections: readonly ActivationSection[],
): number {
  const section = findSection(sections, sectionId);
  const total = section.manual + section.auto;
  if (total === 0) return 100;
  const filledManual = Math.round(section.manual * sectionFill(report, sectionId, section));
  return Math.round(((section.auto + filledManual) / total) * 100);
}

/** Completion of the whole report, weighted by each section's input count. */
export function reportCompleteness(
  report: ActivationReport,
  sections: readonly ActivationSection[],
): number {
  const ids = report.sections;
  if (ids.length === 0) return 0;
  const resolved = ids.map((id) => ({ id, section: findSection(sections, id) }));
  const totals = resolved.map(({ section }) => section.manual + section.auto);
  const filled = resolved.map(
    ({ id, section }) =>
      section.auto + Math.round(section.manual * sectionFill(report, id, section)),
  );
  const denominator = sum(totals);
  if (denominator === 0) return 0;
  return Math.round((sum(filled) / denominator) * 100);
}

/* ---------------------------------------------------------------- status -- */

export const STATUS_META: Record<ActivationStatus, { kind: StatusKind; icon: string }> = {
  Draft: { kind: 'neutral', icon: 'clock' },
  'In Review': { kind: 'info', icon: 'eye' },
  Approved: { kind: 'good', icon: 'checkCircle' },
  Shared: { kind: 'info', icon: 'share' },
};

const STATUS_FLOW: Record<ActivationStatus, ActivationStatus> = {
  Draft: 'In Review',
  'In Review': 'Approved',
  Approved: 'Shared',
  Shared: 'Shared',
};

/** Reviewers expect at least this much of the report to exist. */
export const REVIEW_THRESHOLD = 60;

export const nextStatus = (status: ActivationStatus): ActivationStatus => STATUS_FLOW[status];

export const advanceLabel = (status: ActivationStatus): string =>
  status === 'Draft'
    ? 'Send for review'
    : status === 'In Review'
      ? 'Approve'
      : 'Share with partner';

/**
 * Whether the report may move to the next status.
 * A draft below the review threshold is held back with a reason.
 */
export function canAdvance(
  report: ActivationReport,
  sections: readonly ActivationSection[],
): { readonly ok: boolean; readonly reason?: string } {
  const pc = reportCompleteness(report, sections);
  if (report.status === 'Draft' && pc < REVIEW_THRESHOLD) {
    return {
      ok: false,
      reason: `Completeness is ${pc}%. Reviewers expect at least ${REVIEW_THRESHOLD}%.`,
    };
  }
  return { ok: true };
}

/* --------------------------------------------------- worked demo maths ----- */

export interface MonthlyPoint {
  readonly label: string;
  readonly value: number;
}

/**
 * Rescales the relative monthly weights so they sum to the season total.
 * Without this the chart is labelled in money but plots percentages.
 */
export function rescaleMonthly(
  monthly: readonly (readonly [string, number])[],
  seasonTotal: number,
): readonly MonthlyPoint[] {
  const weightSum = sum(monthly.map(([, w]) => w));
  if (weightSum === 0) return monthly.map(([label]) => ({ label, value: 0 }));
  return monthly.map(([label, w]) => ({
    label,
    value: round((w / weightSum) * seasonTotal, 2),
  }));
}

export interface CompetitionTotals {
  readonly matches: number;
  readonly minutes: number;
  readonly value: number;
  /** TV reach ÷ matches. A per-match average, NOT a column sum. */
  readonly averageReachPerMatch: number;
}

export function competitionTotals(
  competitions: readonly DemoCompetition[],
  tvReachMillions: number,
): CompetitionTotals {
  const matches = sum(competitions.map((c) => c.matches));
  return {
    matches,
    minutes: sum(competitions.map((c) => c.minutes)),
    value: round(sum(competitions.map((c) => c.value)), 2),
    averageReachPerMatch: matches === 0 ? 0 : Math.round((tvReachMillions * 1e6) / matches),
  };
}

/** Social totals across the platform table. */
export function platformTotals(demo: DemoActivationReport): {
  readonly reach: number;
  readonly engagements: number;
} {
  return {
    reach: round(sum(demo.platforms.map((p) => p.reach)), 1),
    engagements: Math.round(sum(demo.platforms.map((p) => p.engagements))),
  };
}

/** Mean utilisation of the sampled matchdays — the headline the chart supports. */
export const meanUtilisation = (games: readonly (readonly [string, number])[]): number =>
  Math.round(mean(games.map(([, v]) => v)));

/* ------------------------------------------ generated executive summary ---- */

export interface ExecSummary {
  readonly mediaValueM: number;
  readonly activations: number;
  readonly reachB: number;
  readonly yoyPct: number;
  readonly highlights: readonly string[];
}

/**
 * Deterministic executive-summary figures for a report in the builder.
 *
 * Seeded from the report id, so the numbers are stable across reloads and
 * differ per report. This is preview content for an unfilled section, and the
 * screen labels it as generated.
 */
export function generateExecSummary(reportId: string): ExecSummary {
  const r = rng(`exec:${reportId}`);
  const mediaValueM = round(6 + r() * 14, 1);
  const activations = 8 + Math.floor(r() * 20);
  const reachB = round(0.6 + r() * 2.2, 1);
  const yoyPct = Math.round(4 + r() * 26);
  const europeanShare = Math.round(30 + r() * 25);
  const socialGrowth = Math.round(60 + r() * 80);
  const utilisation = Math.round(84 + r() * 14);
  return {
    mediaValueM,
    activations,
    reachB,
    yoyPct,
    highlights: [
      `European competition drove ${europeanShare}% of total media value.`,
      `Kit launch achieved record social engagement, up ${socialGrowth}% on the previous year.`,
      `Stadium hospitality utilisation reached ${utilisation}% across all matches.`,
    ],
  };
}
